/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Grid,
  TextField,
  Autocomplete,
  MenuItem,
  Typography,
  Stack,
  Button,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  ChangeEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { collection, DocumentData } from 'firebase/firestore';
import React from 'react';
import { formValues } from '../../../utils/types';
import QualificationDialog from '../QualificationDialog';
import { fplCalculator } from '../../../utils/utilFuntions';
import theme from '../../../theme';
import { useCollection } from 'react-firebase-hooks/firestore';
import { db } from '../../../firebase-setup/firebase';

const PDP_MAPD_FORMULARY_QUESTION_ID = 'pdpMapdFormulary';
const PDP_MAPD_FORMULARY_QUESTION_TEXT =
  'Is [Med Name] listed on your PDP or MAPD Formulary?';

function SecondStep({
  medicationsList,
  values,
  setValues,
  setValidStep,
  numMeds,
  setNumMeds,
}: {
  medicationsList: DocumentData[] | null;
  values: formValues;
  setValues: React.Dispatch<React.SetStateAction<formValues>>;
  setValidStep: React.Dispatch<React.SetStateAction<boolean>>;
  numMeds: number;
  setNumMeds: React.Dispatch<React.SetStateAction<number>>;
}) {
  const [medicationStrengths, setMedicationStrengths] = useState<
    Array<{ strength: string; price: string }[] | null>
  >(Array(numMeds).fill(null));
  const [currentMedName, setCurrnetMedName] = useState<string>('');

  const loadedParams = useRef<boolean>(false);
  const [medCosts, setMedCosts] = useState<Array<string> | null>(
    Array(numMeds).fill('')
  );
  const [medicationDetailsText, setMedicationDetailsText] = useState<
    Array<string>
  >(Array(numMeds).fill(''));
  const [openEligibilityDialog, setOpenEligibilityDialog] =
    useState<boolean>(false);
  const [eligibilityDialogTitle, setEligibilityDialogTitle] = useState<
    string | undefined
  >(undefined);
  const [eligibilityDialogSubtitle, setEligibilityDialogSubtitle] = useState<
    string | undefined
  >(undefined);
  const [isFirstTextFieldChanged, setIsFirstTextFieldChanged] = useState([
    false,
    false,
    false,
    false,
  ]);
  const [medicareExtraDetails, loadingMedicareExtraDetails] = useCollection(
    collection(db, 'medicareExtraDetails'),
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );
  const [medicareExtraDetailsList, setMedicareExtraDetailsList] = useState<
    DocumentData[] | null
  >(null);
  const [isPharmacyExceeds, setIsPharmacyExceeds] = useState<boolean>(false);

  const filterMedicationsOptions = (options: any[], state: any) => {
    const inputValue = state.inputValue;
    return options.filter((option) => {
      if (
        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.genericName.toLowerCase().includes(inputValue.toLowerCase())
      ) {
        return option;
      }
      return false;
    });
  };
  const clientFPL = fplCalculator(
    parseInt(values.patient.numinhouse),
    parseInt(values.patient.income as string)
  );

  const getMedCost = useCallback(
    (medication: string, strength: string) => {
      const med = medicationsList?.find((item) => item.name === medication);
      if (!med) return null;

      const medOption = med.options.find(
        (item: { strength: string; price: string }) =>
          item.strength === strength
      );
      if (!medOption) return null;
      return medOption.price;
    },
    [medicationsList]
  );

  const handleClose = () => {
    setOpenEligibilityDialog(false);
    setEligibilityDialogTitle(undefined);
    setEligibilityDialogSubtitle(undefined);
  };
  const [medExist, setMedExist] = useState<boolean>(false);

  const requiredCheck = useCallback(() => {
    // Start with assumption that form is valid
    let isValid = true;

    if (values.patient.insurance.company === 'medicare') {
      // Get all the questions that are required based on selected medications
      const requiredQuestions: string[] = [];

      // Add required questions based on selected medications
      values.orders.forEach((order) => {
        if (order.medicationName) {
          const medName = order.medicationName.split(' (')[0];
          const selectedMed = medicationsList?.find(
            (item) => item.name === medName
          );
          // App-only: Bausch formulary question
          if (selectedMed?.manufacture === 'Bausch') {
            requiredQuestions.push(PDP_MAPD_FORMULARY_QUESTION_ID);
          }
          if (medicareExtraDetailsList) {
            medicareExtraDetailsList.forEach((detail) => {
              if (detail.medications?.includes(medName)) {
                if (
                  detail.id === 'donutHole' &&
                  values.patient.medicare.partD === 'no' &&
                  values.patient.medicare.advantage === 'no'
                ) {
                  return;
                }

                if (
                  detail.id === 'spent7.5' &&
                  values.patient.medicare.partD === 'no'
                ) {
                  return;
                }

                requiredQuestions.push(detail.id);
              }
            });
          }
        }
      });

      requiredQuestions.forEach((questionId) => {
        // App-only: Bausch PDP/MAPD formulary (no Firestore doc)
        if (questionId === PDP_MAPD_FORMULARY_QUESTION_ID) {
          const userAnsweredYes =
            values.patient.medicareExtraDetails[PDP_MAPD_FORMULARY_QUESTION_ID];
          if (userAnsweredYes) {
            isValid = false;
          }
          return;
        }
        const questionDetail = medicareExtraDetailsList?.find(
          (detail) => detail.id === questionId
        );

        if (questionDetail) {
          const userAnswer = values.patient.medicareExtraDetails[questionId]
            ? 'yes'
            : 'no';
          const isAnswerValid = userAnswer === questionDetail.requiredAnswer;

          if (!isAnswerValid) {
            isValid = false;
          }
        }
      });
    }

    for (let i = 0; i < numMeds; i++) {
      // First medication is required
      if (values.orders[i]?.medicationName === '' && i === 0) {
        isValid = false;
      }
      // If medication name is provided but strength is missing, form is invalid
      else if (
        values.orders[i]?.medicationName !== '' &&
        values.orders[i]?.medicationStrength === ''
      ) {
        isValid = false;
      }
    }

    setValidStep(isValid);
  }, [
    numMeds,
    values.patient.insurance.company,
    values.patient.medicareExtraDetails,
    values.patient.medicare.partD,
    values.patient.medicare.advantage,
    values.orders,
    medicareExtraDetailsList,
    medicationsList,
    setValidStep,
  ]);

  useEffect(() => {
    requiredCheck();
  }, [values.orders, values.patient.medicareExtraDetails, requiredCheck]);
  // Create packages name for pdf creation in salesforce
  const assignMedicareFilesData = useCallback(
    (
      medicationName: string,
      medicationDelivery: string,
      manufacture: string,
      index: number
    ) => {
      if (
        values.patient.insurance.company !== 'medicare' ||
        !medicareExtraDetailsList
      )
        return;

      const isMedicarePartC = values.patient.medicare.advantage === 'yes';
      const partDValue = values.patient.medicare.partD;
      // const isBothSSC = values.patient.medicare.bothSSC === 'yes'; // TODO: restore when reverting BOTH override
      
      // Determine if there's prescription coverage based on partD value
      const hasPrescriptionCoverage = 
        (isMedicarePartC && partDValue === 'MA-PD') || 
        (!isMedicarePartC && partDValue === 'PDP');
      
      let docGenPackage = medicationName;
      let faxPackage = `${
        medicationDelivery === 'Healthcare Provider Office' ? 'Office' : 'Home'
      }_Medicare_Fax`;
      let faxReminderPackage = `${
        medicationDelivery === 'Healthcare Provider Office' ? 'Office' : 'Home'
      }_Medicare_Fax_Reminder`;
      let faxManufacturePackage = `${
        medicationDelivery === 'Healthcare Provider Office' ? 'Office' : 'Home'
      }_Medicare_Fax_Manufacture`;
      let reconsiderationPackage = `_Reconsideration_Fax`;
      if (hasPrescriptionCoverage) {
        docGenPackage += '_Yes';
      } else {
        docGenPackage += '_No';
      }
      const medicationIncludeExtra = medicareExtraDetailsList.find((med) =>
        med.medications.includes(medicationName)
      );
      if (medicationIncludeExtra) {
        const extraDetailType = medicationIncludeExtra.id;
        switch (extraDetailType) {
          case 'spent7.5':
            docGenPackage += '_7.5PS';
            faxPackage = '7.5PS_' + faxPackage;
            faxReminderPackage = '7.5PS_' + faxReminderPackage;
            faxManufacturePackage = '7.5PS_' + faxManufacturePackage;
            reconsiderationPackage = '7.5PS' + reconsiderationPackage;
            break;
          case 'pharmacyExceeds4':
            docGenPackage += '_4PS';
            faxPackage = '4PS_' + faxPackage;
            faxReminderPackage = '4PS_' + faxReminderPackage;
            faxManufacturePackage = '4PS_' + faxManufacturePackage;
            reconsiderationPackage = '4PS' + reconsiderationPackage;
            break;
          case 'pharmacyExceeds3':
            docGenPackage += '_3PS';
            faxPackage = '3PS_' + faxPackage;
            faxReminderPackage = '3PS_' + faxReminderPackage;
            faxManufacturePackage = '3PS_' + faxManufacturePackage;
            reconsiderationPackage = '3PS' + reconsiderationPackage;
            break;
          case 'spend600':
            docGenPackage += '_600PS';
            faxPackage = '600PS_' + faxPackage;
            faxReminderPackage = '600PS_' + faxReminderPackage;
            faxManufacturePackage = '600PS_' + faxManufacturePackage;
            reconsiderationPackage = '600PS' + reconsiderationPackage;
            break;
          case 'spend1000':
            docGenPackage += '_1KPS';
            faxPackage = '1KPS_' + faxPackage;
            faxReminderPackage = '1KPS_' + faxReminderPackage;
            faxManufacturePackage = '1KPS_' + faxManufacturePackage;
            reconsiderationPackage = '1KPS' + reconsiderationPackage;
            break;
        }
      } else if (manufacture === 'Pfizer') {
        docGenPackage += '_PFZ';
        faxPackage = 'PFZ_' + faxPackage;
        faxReminderPackage = 'PFZ_' + faxReminderPackage;
        faxManufacturePackage = 'PFZ_' + faxManufacturePackage;
      } else {
        docGenPackage += '_NML';
        faxPackage = 'NML_' + faxPackage;
        faxReminderPackage = 'NML_' + faxReminderPackage;
        faxManufacturePackage = 'NML_' + faxManufacturePackage;
      }

      // Determine docGenPackage suffix based on Part C and partD values
      if (isMedicarePartC) {
        // Part C = yes
        if (partDValue === 'MA-PD') {
          // MAPD (drug coverage included in Advantage) = Yes
          docGenPackage += '-C_CYRX';
        } else if (partDValue === 'no-partD') {
          // No drug coverage
          docGenPackage += '-C_CNRX';
        } else {
          // Default fallback
          docGenPackage += '_NML';
        }
      } else {
        // Part C = no
        if (partDValue === 'PDP') {
          // PDP = Yes
          docGenPackage += '-D_MPD';
        } else if (partDValue === 'no-prescription-coverage') {
          // No prescription coverage
          docGenPackage += '_NML';
        } else {
          // Default fallback
          docGenPackage += '_NML';
        }
      }

      // TODO: temporarily force BOTH for all docgens
      docGenPackage += '_BOTH';

      if (reconsiderationPackage !== '_Reconsideration_Fax') {
        setValues((prev) => ({
          ...prev,
          orders: prev.orders.map((order, i) =>
            i === index
              ? {
                  ...order,
                  docGenPackage: docGenPackage,
                  medicareFaxPackage: faxPackage,
                  medicareFaxReminderPackage: faxReminderPackage,
                  medicareFaxManufacturerPackage: faxManufacturePackage,
                  medicareReconsiderationPackage: reconsiderationPackage,
                }
              : order
          ),
        }));
      } else {
        setValues((prev) => ({
          ...prev,
          orders: prev.orders.map((order, i) =>
            i === index
              ? {
                  ...order,
                  docGenPackage: docGenPackage,
                  medicareFaxPackage: faxPackage,
                  medicareFaxReminderPackage: faxReminderPackage,
                  medicareFaxManufacturerPackage: faxManufacturePackage,
                }
              : order
          ),
        }));
      }
    },
    [
      values.patient.insurance.company,
      values.patient.medicare.partD,
      values.patient.medicare.advantage,
      values.patient.medicare.bothSSC,
      values.patient.numinhouse,
      medicareExtraDetailsList,
      setValues,
    ]
  );

  const validateMedicationEligibility = useCallback(
    (med: DocumentData, index: number, fpl: number) => {
      const isXolair = med.name === 'Xolair';
      const isNotEligible = isXolair
        ? parseInt(values.patient.income as string) > 150000
        : fpl > med.fpl;
      if (isNotEligible) {
        setOpenEligibilityDialog(true);
        setValues((prev) => ({
          ...prev,
          orders: prev.orders.map((o, i) =>
            i === index
              ? {
                  ...o,
                  medicationName: '',
                  medicationStrength: '',
                }
              : o
          ),
        }));
        setMedicationStrengths((prev) => {
          const newStrengths = [...prev];
          newStrengths[index] = null;
          return newStrengths;
        });
        return false;
      }
      return true;
    },
    [setValues, values.patient.income]
  );
  const handleMedicationChange = useCallback(
    async (value: string | null, index: number, isFromQuery: boolean) => {
      setMedExist(false);
      medCosts![index] = 'Select Medication & Strength';
      const selectedMed = medicationsList!.find(
        (item) =>
          item.name ===
          (value as string)?.slice(0, (value as string).indexOf(' ('))
      );
      if (!selectedMed) return;
      if (!isFromQuery) {
        // Check if the medication is already chosen
        const isMedAlreadyChosen = values.orders.some(
          (order) => order.medicationName === value
        );
        if (isMedAlreadyChosen) {
          setMedExist(true);
          setOpenEligibilityDialog(true);
          return;
        }
      }
      setValues((prev) => ({
        ...prev,
        orders: prev.orders.map((order, i) =>
          i === index
            ? {
                ...order,
                medicationName: value as string,
                medicationDelivery: selectedMed.medicationDelivery,
              }
            : order
        ),
      }));
      assignMedicareFilesData(
        selectedMed.name,
        selectedMed.medicationDelivery,
        selectedMed.manufacture,
        index
      );
      setCurrnetMedName(selectedMed.name);

      // Set medication strengths before eligibility check so they're available even if eligibility fails
      setMedicationStrengths((prev) => {
        const newStrengths = [...prev];
        newStrengths[index] = selectedMed.options || null;
        return newStrengths;
      });

      if (selectedMed.brand) {
        let isntEligible = false;
        const partDValue = values.patient.medicare.partD;
        const isPartDBasedEligibility = 
          selectedMed.name === 'Ozempic' || 
          selectedMed.name === 'Turalio' || 
          selectedMed.name === 'Vanflyta' ||
          selectedMed.name.includes('Eylea') ||
          selectedMed.manufacture === 'Pfizer';
        
        if (selectedMed.name === 'Xolair') {
          Number(values.patient.income) < 150000
            ? (isntEligible = false)
            : (isntEligible = true);
        } else if (
          selectedMed.name === 'Ozempic' || 
          selectedMed.name === 'Turalio' || 
          selectedMed.name === 'Vanflyta' ||
          selectedMed.name.includes('Eylea')
        ) {
          // DONT QUALIFY IF YES PDP or MAPD
          // These medications don't qualify if partD is 'PDP' or 'MA-PD'
          isntEligible = partDValue === 'PDP' || partDValue === 'MA-PD';
        } else if (selectedMed.manufacture === 'Pfizer') {
          // DONT QUALIFY IF NO PDP nor MAPD
          // Pfizer medications don't qualify if partD is NOT 'PDP' and NOT 'MA-PD'
          isntEligible = partDValue !== 'PDP' && partDValue !== 'MA-PD';
        } else {
          isntEligible = clientFPL > selectedMed.fpl;
        }
        
        // Only run FPL-based validation for medications that don't have partD-based eligibility
        // Medications with partD-based eligibility (Ozempic, Turalio, Vanflyta, Eylea, Pfizer) 
        // should skip FPL validation to avoid clearing strengths
        if (!isPartDBasedEligibility && !validateMedicationEligibility(selectedMed, index, clientFPL)) {
          return;
        }
        
        // For partD-based eligibility medications, clear medication if ineligible
        if (isPartDBasedEligibility && isntEligible) {
          setOpenEligibilityDialog(true);
          setValues((prev) => ({
            ...prev,
            orders: prev.orders.map((o, i) =>
              i === index
                ? {
                    ...o,
                    medicationName: '',
                    medicationStrength: '',
                  }
                : o
            ),
          }));
          setMedicationStrengths((prev) => {
            const newStrengths = [...prev];
            newStrengths[index] = null;
            return newStrengths;
          });
          return;
        }
        
        setOpenEligibilityDialog(isntEligible);
      }
      setMedicationDetailsText((prev) => {
        const newDetails = [...prev];
        if (selectedMed.brand) {
          newDetails[index] =
            'Once your healthcare provider complete our simple steps, expect to get your medication directly from the manufacturer in a 90 days supply in about 3 week';
        } else {
          newDetails[index] =
            'Once your healthcare provider complete our simple steps, expect to get your medication directly from the manufacturer in a 90 days supply in about 1 week';
        }
        return newDetails;
      });
      setIsFirstTextFieldChanged((prev) => {
        const newIsFirstTextFieldChanged = [...prev];
        newIsFirstTextFieldChanged[index] = true;
        return newIsFirstTextFieldChanged;
      });
    },

    [
      assignMedicareFilesData,
      clientFPL,
      medCosts,
      medicationsList,
      setValues,
      validateMedicationEligibility,
      values.orders,
      values.patient.income,
      values.patient.medicare.partD,
    ]
  );
  const handleStrengthChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, index: number) => {
      const selectedStrength = e.target.value;
      
      setValues((prev) => {
        const currentOrder = prev.orders[index];
        const medicationName = currentOrder?.medicationName || '';
        const medicationNameOnly = medicationName.includes(' (')
          ? medicationName.slice(0, medicationName.indexOf(' ('))
          : medicationName;
        
        // Update med costs after state update using setTimeout to avoid nested updates
        setTimeout(() => {
          setMedCosts((prevCosts) => {
            const newCosts = [...(prevCosts ?? [])];
            newCosts[index] = getMedCost(medicationNameOnly, selectedStrength);
            return newCosts;
          });
        }, 0);
        
        return {
          ...prev,
          orders: prev.orders.map((order, i) =>
            i === index
              ? {
                  ...order,
                  medicationStrength: selectedStrength,
                }
              : order
          ),
        };
      });
    },
    [setValues, getMedCost]
  );

  const handleMedicareExtraChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.value === 'yes' ? true : false;

      setValues((prev) => ({
        ...prev,
        patient: {
          ...prev.patient,
          medicareExtraDetails: {
            ...prev.patient.medicareExtraDetails,
            [name]: value,
          },
        },
      }));
    },
    [setValues]
  );
  const extraMedicareFields = useMemo(
    () => (index: number) => {
      if (!values.orders[index]?.medicationName) return null;

      const medName = values.orders[index]?.medicationName.split(' (')[0];
      const selectedMed = medicationsList?.find(
        (item) => item.name === medName
      );

      // App-only: Bausch PDP/MAPD formulary question
      if (selectedMed?.manufacture === 'Bausch') {
        const userAnswer = values.patient.medicareExtraDetails[
          PDP_MAPD_FORMULARY_QUESTION_ID
        ]
          ? 'yes'
          : 'no';
        const requiredAnswer = 'no';
        const isAnswerValid = userAnswer === requiredAnswer;
        const questionText = PDP_MAPD_FORMULARY_QUESTION_TEXT.replace(
          '[Med Name]',
          medName
        );

        return (
          <Grid item xs={12}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel id={PDP_MAPD_FORMULARY_QUESTION_ID}>
                  {questionText}
                  {!isAnswerValid && (
                    <Typography
                      color="error"
                      variant="caption"
                      display="block"
                      sx={{ mt: 1 }}>
                      This medication is listed on your PDP or MAPD Formulary.
                      You do not qualify for this program.
                    </Typography>
                  )}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby={PDP_MAPD_FORMULARY_QUESTION_ID}
                  value={userAnswer}
                  name={PDP_MAPD_FORMULARY_QUESTION_ID}
                  sx={{ mt: 2 }}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleMedicareExtraChange(e);
                    if (e.target.value !== requiredAnswer) {
                      setCurrnetMedName(medName);
                      setEligibilityDialogTitle('You do not qualify');
                      setEligibilityDialogSubtitle(
                        `${medName} is listed on your PDP or MAPD Formulary. This program is for medications not on your formulary.`
                      );
                      setOpenEligibilityDialog(true);
                    }
                    requiredCheck();
                  }}>
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="No"
                    sx={{
                      pr: 2,
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      marginLeft: '0rem',
                      ...(userAnswer === 'no' && !isAnswerValid && {
                          borderColor: 'error.main',
                        }),
                    }}
                  />
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                    sx={{
                      pr: 2,
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      marginLeft: '0rem',
                      ...(userAnswer === 'yes' && !isAnswerValid && {
                        borderColor: 'error.main',
                      }),
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          </Grid>
        );
      }

      if (!medicareExtraDetailsList) return null;

      const extraFieldDetails = medicareExtraDetailsList.filter((med) =>
        med.medications?.includes(medName)
      );
      if (extraFieldDetails.length === 0) return null;
      if (extraFieldDetails[0].id === 'pharmacyExceeds4') return null;
      if (
        extraFieldDetails[0].id === 'donutHole' &&
        values.patient.medicare.partD === 'no' &&
        values.patient.medicare.advantage === 'no'
      )
        return null;

      if (
        extraFieldDetails[0].id === 'spent7.5' &&
        values.patient.medicare.partD === 'no'
      )
        return null;

      // Check if this answer is valid
      const userAnswer = values.patient.medicareExtraDetails[
        `${extraFieldDetails[0].id}`
      ]
        ? 'yes'
        : 'no';
      const isAnswerValid = userAnswer === extraFieldDetails[0].requiredAnswer;

      let percentage = '';
      switch (extraFieldDetails[0].id) {
        case 'spent7.5':
          percentage = '7.5%';
          break;
        case 'pharmacyExceeds3':
          percentage = '3%';
          break;
        case 'spend600':
          percentage = '$600';
          break;
        case 'spend1000':
          percentage = '$1,000';
          break;
        default:
          percentage = '';
      }

      return (
        <Grid item xs={12}>
          <Stack spacing={2}>
            {/* Remove required prop to prevent automatic asterisk */}
            <FormControl>
              <FormLabel id={extraFieldDetails[0].id}>
                {extraFieldDetails[0].question}
                {!isAnswerValid && (
                  <Typography
                    color="error"
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}>
                    To receive the medication next year, the household must
                    first spend at least {percentage} of its household yearly
                    income on pharmacy expenses during the next calendar year.
                  </Typography>
                )}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby={extraFieldDetails[0].id}
                value={
                  values.patient.medicareExtraDetails[
                    `${extraFieldDetails[0].id}`
                  ]
                    ? 'yes'
                    : 'no'
                }
                name={extraFieldDetails[0].id}
                sx={{ mt: 2 }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleMedicareExtraChange(e);
                  if (e.target.value !== extraFieldDetails[0].requiredAnswer) {
                    setOpenEligibilityDialog(true);
                  }

                  requiredCheck();
                }}>
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label="No"
                  sx={{
                    pr: 2,
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    marginLeft: '0rem',
                    ...(userAnswer === 'no' &&
                      userAnswer !== extraFieldDetails[0].requiredAnswer && {
                        borderColor: 'error.main',
                      }),
                  }}
                />
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label="Yes"
                  sx={{
                    pr: 2,
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    marginLeft: '0rem',
                    ...(userAnswer === 'yes' &&
                      userAnswer !== extraFieldDetails[0].requiredAnswer && {
                        borderColor: 'error.main',
                      }),
                  }}
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Grid>
      );
    },
    [
      handleMedicareExtraChange,
      medicareExtraDetailsList,
      medicationsList,
      requiredCheck,
      values.orders,
      values.patient.medicare.advantage,
      values.patient.medicare.partD,
      values.patient.medicareExtraDetails,
    ]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const medName = queryParams.get('medicationName');
    if (medName && !loadedParams.current) {
      handleMedicationChange(medName, 0, true);
      loadedParams.current = true;
    }
  }, [handleMedicationChange]);

  useEffect(() => {
    if (values.orders.some((order) => order.medicationName !== '')) {
      values.orders.forEach((order, index) => {
        if (order.medicationName !== '' && order.medicationStrength !== '') {
          const selectedMed = medicationsList?.find(
            (item) =>
              item.name ===
              order.medicationName.slice(0, order.medicationName.indexOf(' ('))
          );
          if (selectedMed) {
            // Only update if strengths haven't been set yet to avoid infinite loops
            setMedicationStrengths((prev) => {
              if (prev[index] === selectedMed.options) {
                return prev; // No change needed
              }
              const newStrengths = [...prev];
              newStrengths[index] = selectedMed.options || null;
              return newStrengths;
            });
            // Only update med costs if not already set to avoid infinite loops
            setMedCosts((prev) => {
              const newCosts = [...(prev ?? [])];
              const medicationNameOnly = order.medicationName.slice(
                0,
                order.medicationName.indexOf(' (')
              );
              const calculatedCost = getMedCost(
                medicationNameOnly,
                order.medicationStrength
              );
              // Only update if the cost is different to prevent infinite loops
              if (newCosts[index] !== calculatedCost) {
                newCosts[index] = calculatedCost;
                return newCosts;
              }
              return prev;
            });
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMedCost, medicationsList]);
  useEffect(() => {
    if (medicareExtraDetails && !loadingMedicareExtraDetails) {
      setMedicareExtraDetailsList(
        medicareExtraDetails.docs.map((doc) => doc.data())
      );
    }
  }, [loadingMedicareExtraDetails, medicareExtraDetails]);
  // Helper function to check partD-based eligibility
  const checkPartDEligibility = useCallback(
    (med: DocumentData, partDValue: string): boolean => {
      const isPartDBasedEligibility =
        med.name === 'Ozempic' ||
        med.name === 'Turalio' ||
        med.name === 'Vanflyta' ||
        med.name.includes('Eylea') ||
        med.manufacture === 'Pfizer';

      if (!isPartDBasedEligibility) {
        return true; // Not a partD-based medication, so eligible by default for this check
      }

      if (
        med.name === 'Ozempic' ||
        med.name === 'Turalio' ||
        med.name === 'Vanflyta' ||
        med.name.includes('Eylea')
      ) {
        // DONT QUALIFY IF YES PDP or MAPD
        return partDValue !== 'PDP' && partDValue !== 'MA-PD';
      } else if (med.manufacture === 'Pfizer') {
        // DONT QUALIFY IF NO PDP nor MAPD
        return partDValue === 'PDP' || partDValue === 'MA-PD';
      }

      return true;
    },
    []
  );

  useEffect(() => {
    const updatedFPL = fplCalculator(
      parseInt(values.patient.numinhouse),
      parseInt(values.patient.income as string)
    );
    const partDValue = values.patient.medicare.partD;

    // Collect indices of medications that need to be cleared
    const indicesToClear: number[] = [];
    let firstIneligibleMedName: string | null = null;

    values.orders.forEach((order, index) => {
      if (!order.medicationName) return;

      const medName = order.medicationName?.split(' (')[0];
      const selectedMed = medicationsList?.find(
        (item) => item.name === medName
      );

      if (selectedMed) {
        // Check FPL-based eligibility
        validateMedicationEligibility(selectedMed, index, updatedFPL);

        // Check partD-based eligibility for medications that require it
        const isPartDBasedEligibility =
          selectedMed.name === 'Ozempic' ||
          selectedMed.name === 'Turalio' ||
          selectedMed.name === 'Vanflyta' ||
          selectedMed.name.includes('Eylea') ||
          selectedMed.manufacture === 'Pfizer';

        if (isPartDBasedEligibility && partDValue) {
          const isEligible = checkPartDEligibility(selectedMed, partDValue);
          if (!isEligible) {
            indicesToClear.push(index);
            if (!firstIneligibleMedName) {
              firstIneligibleMedName = selectedMed.name;
            }
          }
        }
      }
    });

    // Batch clear all ineligible medications
    if (indicesToClear.length > 0) {
      if (firstIneligibleMedName) {
        setOpenEligibilityDialog(true);
        setCurrnetMedName(firstIneligibleMedName);
      }

      setValues((prev) => ({
        ...prev,
        orders: prev.orders.map((o, i) =>
          indicesToClear.includes(i)
            ? {
                ...o,
                medicationName: '',
                medicationStrength: '',
              }
            : o
        ),
      }));

      setMedicationStrengths((prev) => {
        const newStrengths = [...prev];
        indicesToClear.forEach((index) => {
          newStrengths[index] = null;
        });
        return newStrengths;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.patient.income,
    values.patient.numinhouse,
    values.patient.medicare.partD,
    checkPartDEligibility,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(values.orders),
    medicationsList,
    values.patient.insurance.company,
    validateMedicationEligibility,
  ]);

  useEffect(() => {
    const isPharmacyExceeds = values.patient.medicareExtraDetails
      ? Object.values(values.patient.medicareExtraDetails).some(
          (value) => value === true
        )
      : false;

    setIsPharmacyExceeds(isPharmacyExceeds);
  }, [medicareExtraDetailsList, values.patient.medicareExtraDetails]);

  return (
    <>
      <Box className="tprx-card">
        <Stack>
          <Typography
            variant="h2"
            textAlign={'center'}
            color={theme.palette.primary.main}>
            Great, now pick the medications you need
          </Typography>
          <Typography variant="h3" textAlign={'center'}>
            Based on your earlier details, we'll show you your plan options and
            costs - or even if it's free!
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          {numMeds > 0 &&
            medicationsList &&
            Array.from({ length: numMeds }, (_, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    filterOptions={filterMedicationsOptions}
                    disablePortal
                    options={
                      values.patient.insurance.company ===
                      "I don't have insurance coverage at this time"
                        ? medicationsList.filter(
                            (med) => med.manufacture !== 'AstraZeneca'
                          )
                        : medicationsList
                    }
                    getOptionLabel={(option: any) =>
                      `${option.name} (${option.genericName})`
                    }
                    value={
                      (values.orders[index]?.medicationName &&
                        medicationsList.find(
                          (option) =>
                            option.name ===
                            values.orders[index].medicationName.split(' (')[0]
                        )) ||
                      null
                    }
                    onChange={(_e, value: any) => {
                      handleMedicationChange(
                        !value ? '' : `${value.name} (${value.genericName})`,
                        index,
                        false
                      );
                      if (!value) {
                        handleStrengthChange(
                          {
                            target: { value: '' },
                          } as ChangeEvent<HTMLInputElement>,
                          index
                        );
                      }
                    }}
                    renderOption={(props, option: any) => {
                      const { key, ...optionProps } = props;
                      return (
                        <Stack
                          key={key}
                          component="li"
                          {...optionProps}
                          direction={'row'}>
                          <Stack direction={'column'} mr={'auto'}>
                            <Typography variant="body1">
                              {option.name}
                            </Typography>
                            <Typography variant="body2">
                              ({option.genericName})
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              backgroundColor: option.brand
                                ? theme.palette.primary.dark
                                : theme.palette.primary.main,
                              color: theme.palette.common.white,
                              padding: '0.2rem 0.5rem',
                              borderRadius: '0.5rem',
                              marginLeft: '2rem',
                            }}>
                            {option.brand ? 'Brand' : 'Generic'}
                          </Typography>
                        </Stack>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required={index === 0}
                        fullWidth
                        name={'medicationName'}
                        value={
                          (values.orders[index] &&
                            values.orders[index]['medicationName']) ||
                          null
                        }
                        label={'Select Medication'}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    required={isFirstTextFieldChanged[index]}
                    key={`choosestrength-${index}`}
                    fullWidth
                    select
                    name={`medicationStrength`}
                    value={values.orders[index]?.medicationStrength || ''}
                    label={'Choose Medication Strength'}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      handleStrengthChange(e, index);
                    }}
                    variant="outlined">
                    {medicationStrengths[index] ? (
                      medicationStrengths[index]!.map((option) => (
                        <MenuItem key={option.strength} value={option.strength}>
                          {option.strength}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value={''}>Choose Medication First</MenuItem>
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack justifyContent={'center'} height={'100%'}>
                    <Typography fontWeight={600}>
                      {(medCosts && medCosts[index]) ||
                        'Select Medication & Strength'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color={'textSecondary'}
                    fontWeight={500}>
                    {medicationDetailsText[index]}
                  </Typography>
                </Grid>
                {values.patient.insurance.company === 'medicare' &&
                  extraMedicareFields(index)}
              </React.Fragment>
            ))}
        </Grid>
        <Stack
          spacing={2}
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          justifyContent={'space-between'}
          width={'100%'}>
          <Button
            variant="outlined"
            onClick={() => {
              setNumMeds((prev) => prev + 1);
              values.orders.push({
                doctorName: '',
                medicationName: '',
                medicationStrength: '',
                medicationDelivery: '',
              });
              setMedicationStrengths((prev) => [...prev, null]);
            }}>
            Add Medication
          </Button>
        </Stack>
      </Box>
      <QualificationDialog
        open={openEligibilityDialog}
        handleClose={handleClose}
        qulificationGroup={'who are under 18'}
        isEligible={true}
        isPharmacyExceeds={isPharmacyExceeds}
        medName={currentMedName || ''}
        medExist={medExist}
        customTitle={eligibilityDialogTitle}
        customSubtitle={eligibilityDialogSubtitle}
        isPfizerMedication={
          medicationsList?.some(
            (med) => med.name === currentMedName && med.manufacture === 'Pfizer'
          ) || false
        }
      />
    </>
  );
}
export default SecondStep;
